import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = chargement, false = non connecté
  const [favorites, setFavorites] = useState({});

  const loadFavorites = useCallback(async () => {
    try {
      const { data } = await api.get("/favorites");
      const map = {};
      data.forEach((f) => {
        map[`${f.item_type}:${f.item.slug}`] = f.item;
      });
      setFavorites(map);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) loadFavorites();
    else setFavorites({});
  }, [user, loadFavorites]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
    toast.success("Tu es déconnecté·e");
  };

  const isFavorite = (type, slug) => Boolean(favorites[`${type}:${slug}`]);

  const toggleFavorite = async (type, item) => {
    if (!user) {
      toast.info("Connecte-toi pour enregistrer tes favoris");
      return false;
    }
    const key = `${type}:${item.slug}`;
    if (favorites[key]) {
      await api.delete(`/favorites/${type}/${item.slug}`);
      setFavorites((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      toast.success("Retiré de tes favoris");
    } else {
      await api.post("/favorites", { item_type: type, item_slug: item.slug });
      setFavorites((prev) => ({ ...prev, [key]: item }));
      toast.success("Ajouté à tes favoris");
    }
    return true;
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refreshUser, favorites, isFavorite, toggleFavorite }}>
      {children}
    </AuthContext.Provider>
  );
}
